import moment from 'moment';
import { FormattedMessage } from 'react-intl';
import { AlertInfoIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import { validatePhoneNumber } from '../../../../utils/validations';
import { DEFAULT_TRACS_VOUCHER_ID } from '../../../../constants/hudVoucher';

const NAME_PATTERN = /^[a-zA-Z\s\-.]*$/;
const TITLE_PATTERN = /^[a-zA-Z\s&\-/.]*$/;

export const getFormFields = ({
  voucherMonthMoment,
  messages,
  intl,
  states,
  isStatesLoading,
  isResubmission,
  lastSubmissionDate,
}) => [
  {
    variant: 'text',
    name: 'voucherId',
    label: <FormattedMessage {...messages.voucherIdLabel} />,
    InputLabelProps: {
      TooltipProps: {
        title: intl.formatMessage({ ...messages.voucherIdTooltip }),
        placement: 'right',
        variant: 'light',
        children: <AlertInfoIcon />,
      },
    },
    disabled: !isResubmission,
    rules: {
      validate: (value) => {
        if (isResubmission && value === DEFAULT_TRACS_VOUCHER_ID) {
          return intl.formatMessage(messages.voucherIdError, {
            defaultVoucherId: DEFAULT_TRACS_VOUCHER_ID,
          });
        }
        return true;
      },
      required: true,
    },
    GridProps: {
      xs: 12,
    },
  },
  {
    variant: 'text',
    name: 'name',
    label: <FormattedMessage {...messages.nameLabel} />,
    rules: {
      required: true,
      maxLength: 50,
      validate: (value) =>
        !value ||
        NAME_PATTERN.test(value) ||
        intl.formatMessage(messages.nameInvalidCharsError),
    },
    GridProps: {
      xs: 12,
    },
  },
  {
    variant: 'text',
    name: 'title',
    label: <FormattedMessage {...messages.titleLabel} />,
    rules: {
      required: true,
      maxLength: 50,
      validate: (value) =>
        !value ||
        TITLE_PATTERN.test(value) ||
        intl.formatMessage(messages.titleInvalidCharsError),
    },
    GridProps: {
      xs: 12,
    },
  },
  {
    variant: 'address',
    name: 'streetAddress',
    label: <FormattedMessage {...messages.streetAddressLabel} />,
    rules: {
      required: true,
    },
    GridProps: {
      xs: 12,
    },
  },
  {
    variant: 'text',
    name: 'city',
    label: <FormattedMessage {...messages.cityLabel} />,
    rules: {
      required: true,
    },
    GridProps: {
      xs: 6,
    },
  },
  {
    variant: 'singleSelect',
    name: 'state',
    label: <FormattedMessage {...messages.stateLabel} />,
    disabled: isStatesLoading,
    options: (Array.isArray(states) ? states : []).map((state) => ({
      text: state.name,
      value: state.id,
    })),
    rules: {
      required: true,
    },
    GridProps: {
      xs: 6,
    },
  },
  {
    variant: 'text',
    name: 'zipCode',
    label: <FormattedMessage {...messages.zipCodeLabel} />,
    placeholder: '',
    inputProps: {
      maxLength: 5,
      inputMode: 'numeric',
      pattern: '[0-9]*',
    },
    rules: {
      required: true,
      validate: (value) => {
        const str = String(value ?? '').trim();
        if (!/^\d{5}$/.test(str)) {
          return intl.formatMessage(messages.zipCodeError);
        }
        return true;
      },
    },
    GridProps: {
      xs: 6,
    },
  },
  {
    variant: 'phone',
    name: 'phoneNumber',
    label: <FormattedMessage {...messages.phoneNumberLabel} />,
    rules: {
      required: true,
      validate: (value) =>
        validatePhoneNumber(value) ||
        intl.formatMessage(messages.phoneNumberError),
    },
    GridProps: {
      xs: 6,
    },
  },
  {
    variant: 'date',
    name: 'date',
    label: <FormattedMessage {...messages.dateLabel} />,
    // The minimum date is either the beginning of the voucher month or the last submission date, whichever is later
    minDate: moment().max(
      lastSubmissionDate
        ? moment(lastSubmissionDate).startOf('day')
        : voucherMonthMoment,
      voucherMonthMoment,
    ),
    maxDate: moment().endOf('day'),
    rules: {
      required: true,
      validate: (value) => {
        if (!value) return true;
        const d =
          typeof value === 'string'
            ? moment(value)
            : value && value.toDate
            ? moment(value.toDate())
            : moment(value);
        const isAfterToday = d.isAfter(moment(), 'day');
        if (isAfterToday) {
          return intl.formatMessage(messages.dateAfterTodayError);
        }
        if (
          lastSubmissionDate &&
          d.isValid() &&
          d.isBefore(lastSubmissionDate, 'day')
        ) {
          return intl.formatMessage(messages.dateBeforeLastSubmissionError);
        }
        const startOfVoucherMonth = voucherMonthMoment.clone().startOf('day');
        const endOfToday = moment().endOf('day');
        if (d.isBefore(startOfVoucherMonth) || d.isAfter(endOfToday)) {
          return intl.formatMessage(messages.dateError);
        }
        return true;
      },
    },
    GridProps: {
      xs: 6,
    },
  },
];
